/* eslint-disable react-hooks/exhaustive-deps */
import { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
// libraries
import { Stack, Button, Menu } from '@mui/material';
// Components
import { addScriptRowData, getCallScriptSection } from 'src/redux/slices/callScript';
import { getPracticeTreatmentList } from 'src/redux/slices/practiceTreatment';
import { getPracticeItem } from 'src/redux/slices/practice';
import { shallowEqual } from 'react-redux';
import useFormsFlags from 'src/hooks/useFormsFlags';
import NavList from './NavList';
import { OpenCallScriptPopup } from '../../fullScreenPopUps';
import { ConsentBuilderPopUp, ReviewLinkPopUp } from '../../dialogPopUp';

import SendFormsDialog from '../../dialogPopUp/SendFormsDialog';
import Iconify from '../../Iconify';
import CustomMenuItem from '../CustomMenuItem';
// Redux
import { clearConsentData } from '../../../redux/slices/lead';
import { openCreateLeadDialog } from '../../../redux/slices/createLeadDialog';
import { dispatch, useSelector } from '../../../redux/store';
// Hooks/routes/utils/constants
import { useAuth, useToggle, useConsentToggle } from '../../../hooks';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { hideScrollbarY } from '../../../utils/cssStyles';
import { allowedTitles } from '../../../constants/commonConstants';
import { localStorageRemoveItem } from '../../../utils/storage-available';
import { isNavItemHidden, titleToSlug } from '../hiddenNavItems';
// style
import { stackStyle } from '../../../styles/Common';

const NavSectionHorizontal = ({ navConfig, sx, ...other }) => {
    const { practiceUser: { results } } = useSelector(state => state.practiceUser);
    const { dentallyEnabledGlobal } = useSelector((state) => state.practice);
    const practice = useSelector((state) => state.practice?.practice);
    const permissions = useSelector((state) => state.permissions?.permissions, shallowEqual);
    const hiddenNavItems = practice?.preferences?.hidden_nav_items || [];
    const filterNHSNav = practice?.preferences?.is_accepting_nhs_patients;
    const choicesData = useSelector((state) => state.practiceTreatment?.choicesData?.results || []);
    const [anchorEl, setAnchorEl] = useState(null);
    const navigate = useNavigate();

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const createHandleClose = () => setAnchorEl(null);

    const { user } = useAuth();

    const getUserList = () => results.filter(el => el.user && el.user.id === user?.pk);

    const {
        toggle: ConsentBuilder,
        onOpen: onOpenConsent,
        onClose: onCloseConsent
    } = useConsentToggle();

    const {
        toggle: openReview,
        onOpen: onOpenReview,
        onClose: onCloseReview
    } = useToggle();

    const {
        toggle: openCallScript,
        onOpen: onOpencallScript,
        onClose: onCloseCallScript
    } = useToggle();

    const { practiceId } = useSelector((state) => state.practiceTreatmentList);

    // Consent form flags — read from feature flags (replaces the old choicesData derivation)
    const { v1Enabled, v2Enabled } = useFormsFlags();
    const [consentV2Open, setConsentV2Open] = useState(false);

    const onCloseCallScriptPopup = () => {
        const commonParams = {
            practiceId,
            limit: 10,
            offset: 0,
        };
        [false, true].forEach((isTemplate) => {
            dispatch(getCallScriptSection({ ...commonParams, isTemplate }));
        });
        onCloseCallScript();
    };

    const handleReviewClick = () => { onOpenReview(); createHandleClose(); };
    const handleClickNewLead = () => {
        localStorageRemoveItem('localTreatmentData');
        dispatch(openCreateLeadDialog());
        createHandleClose();
    };
    const handleClickConsentV1Click = () => {
        onOpenConsent();
        dispatch(clearConsentData());
        createHandleClose();
    };
    const handleClickConsentV2Click = () => {
        setConsentV2Open(true);
        createHandleClose();
    };
    const handleClickStripePaymentClick = () => { navigate(PATH_DASHBOARD.createStripePayment.root); createHandleClose(); };
    const handleClickCallScriptClick = () => {
        dispatch(getCallScriptSection({ practiceId, limit: 10, offset: 0, isTemplate: false }));
        dispatch(getCallScriptSection({ practiceId, limit: 10, offset: 0, isTemplate: true }));
        dispatch(addScriptRowData(''));
        dispatch(getPracticeTreatmentList(practiceId));
        dispatch(getPracticeItem(practiceId));
        onOpencallScript();
        // navigate(PATH_DASHBOARD.user.callScripts, { state: { callScriptPopupOpen: true } });
        createHandleClose();
    };
    const handleClickConsentBuilder = () => { navigate(PATH_DASHBOARD.superAdmin.consent); createHandleClose(); };

    return (
        <>
            <Stack direction='row' sx={stackStyle}>
                <Stack direction='row' sx={stackStyle}>
                    <Stack direction='row' spacing={1} sx={{ ...hideScrollbarY, ...sx }} {...other}>
                        {navConfig.map((group) => (
                            <Stack key={group.subheader} direction='row' flexShrink={0}>
                                {group.items.map((list) => {
                                    const normalisedTitle = list?.title?.toLowerCase?.() || '';
                                    const isSuperAdminItem = normalisedTitle === 'super admin';
                                    const canRenderSuperAdmin = isSuperAdminItem && getUserList()[0]?.user?.is_superuser;
                                    const isAllowed = allowedTitles.includes(normalisedTitle) || canRenderSuperAdmin;

                                    if (!isAllowed) {
                                        return '';
                                    }

                                    // Feature permission check.
                                    // - Default (fail-open): hide only when permissions are loaded
                                    //   AND the feature is explicitly 'hidden'.
                                    // - `requirePermission: true` (fail-closed): hide until the
                                    //   feature is explicitly granted ('view' or 'manage').
                                    if (list.feature) {
                                        const featurePermission = permissions?.[list.feature];
                                        if (list.requirePermission) {
                                            if (featurePermission !== 'view' && featurePermission !== 'manage') {
                                                return '';
                                            }
                                        } else if (permissions && Object.keys(permissions).length > 0) {
                                            if (featurePermission === 'hidden') {
                                                return '';
                                            }
                                        }
                                    }

                                    // Website annotation always visible — sessions stored in our backend

                                    if (normalisedTitle === 'dentally') {
                                        if (!dentallyEnabledGlobal) {
                                            return '';
                                        }
                                    }

                                    // Filter children by feature permissions — same fail-open /
                                    // fail-closed distinction as the parent above.
                                    const featureFilteredChildren = list.children
                                        ? list.children.filter((child) => {
                                            if (!child.feature) return true;
                                            const childPerm = permissions?.[child.feature];
                                            if (child.requirePermission) {
                                                return childPerm === 'view' || childPerm === 'manage';
                                            }
                                            if (permissions && Object.keys(permissions).length > 0) {
                                                return childPerm !== 'hidden';
                                            }
                                            return true;
                                        })
                                        : list.children;

                                    // Practice nav preferences — cosmetic hide for sub-views
                                    const navPrefFilteredChildren = featureFilteredChildren?.filter((child) => {
                                        if (child.divider) return true;
                                        return !isNavItemHidden(titleToSlug(child?.title), hiddenNavItems);
                                    });

                                    // Filter Marketing Brief child by enableClickup
                                    const clickupFilteredChildren = navPrefFilteredChildren?.filter((child) => {
                                        if (child?.title?.toLowerCase() === 'marketing brief') {
                                            const clickupCfg = (choicesData || []).find(
                                                (c) => String(c?.name || '').trim().toLowerCase() === 'clickup config'
                                            );
                                            const enableCu = String(clickupCfg?.metadata?.enableClickup ?? '').trim().toLowerCase();
                                            return enableCu === 'yes' || enableCu === 'true' || enableCu === '1';
                                        }
                                        return true;
                                    });

                                    const shouldFilterNHSChildren = normalisedTitle === 'active leads' || normalisedTitle === 'lead management';
                                    const nhsFilteredChildren = shouldFilterNHSChildren
                                        ? clickupFilteredChildren?.filter((child) => filterNHSNav || child?.title !== 'NHS')
                                        : clickupFilteredChildren;

                                    const filteredList = list.children
                                        ? { ...list, children: nhsFilteredChildren || [] }
                                        : list;

                                    return (
                                        <NavList
                                            key={list.title + list.path}
                                            data={filteredList}
                                            depth={1}
                                            hasChildren={!!list.children}
                                        />
                                    );
                                })}
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
                <Stack direction='row' sx={{ position: 'absolute', right: '30px' }}>
                    <Button
                        sx={{ mb: 0, ml: 1, minWidth: '200px' }}
                        variant='contained'
                        onClick={handleClick}
                        endIcon={<Iconify icon={'eva:arrow-down-fill'} />}
                        startIcon={<Iconify icon={'eva:plus-fill'} />}
                    >
                        Create
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={createHandleClose}
                        sx={{ '.MuiPaper-root': { minWidth: '200px' }, '.MuiMenuItem-root': { fontSize: '0.875rem' } }}
                    >
                        <CustomMenuItem
                            handleClickCallScriptClick={handleClickCallScriptClick}
                            handleClickConsentV1Click={handleClickConsentV1Click}
                            handleClickConsentV2Click={handleClickConsentV2Click}
                            handleClickNewLead={handleClickNewLead}
                            handleClickStripePaymentClick={handleClickStripePaymentClick}
                            handleReviewClick={handleReviewClick}
                            handleClickConsentBuilder={handleClickConsentBuilder}
                            v1Enabled={v1Enabled}
                            v2Enabled={v2Enabled}
                        />
                    </Menu>
                </Stack>
                {ConsentBuilder && v1Enabled && <ConsentBuilderPopUp
                    open={ConsentBuilder}
                    onClose={onCloseConsent}
                />}
                {consentV2Open && <SendFormsDialog
                    open={consentV2Open}
                    onClose={() => setConsentV2Open(false)}
                    practiceId={practiceId}
                />}
                {openReview && <ReviewLinkPopUp
                    open={openReview}
                    onClose={onCloseReview}
                />}
                {openCallScript && <OpenCallScriptPopup
                    open={openCallScript}
                    headerButtons
                    onClose={() => onCloseCallScriptPopup()}
                    type={'callScript'}
                />}
            </Stack>
        </>
    );
};

NavSectionHorizontal.propTypes = {
    navConfig: PropTypes.array,
    sx: PropTypes.object,
};

export default memo(NavSectionHorizontal);
